import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr"
import { pressable } from "../lib/press"
import { detent, landed } from "../lib/feedback"

// Arrival and departure as one range on an always-open calendar — or, for a
// single-day certificate (mode "single"), the one day, picked with one click
// and sent as both. The text field above it only reads back what was picked;
// the calendar is the control. Picking dispatches "date-range-picker:change".
//
// The mode follows the certificate picked (certificate_picker sets it); a
// change rebuilds the calendar, keeping the dates only when they fit the new
// mode (a one-day range is a day; a longer one is not).
export default class extends Controller {
  static targets = ["start", "end", "display", "calendar", "rangeCopy", "singleCopy"]
  static values = {
    minDate: String,
    maxDate: String,
    mode: { type: String, default: "range" }
  }

  // Two months need about this much room side by side.
  static TWO_MONTHS_MIN_WIDTH = 560

  connect() {
    this.build()

    this.resizeObserver = new ResizeObserver(entries => this.fitMonths(entries[0].contentRect.width))
    this.resizeObserver.observe(this.measured)
  }

  disconnect() {
    this.resizeObserver?.disconnect()
    this.teardown()
  }

  get single() {
    return this.modeValue === "single"
  }

  // What the calendar's width is read from: the wrapper it sits in.
  get measured() {
    return this.hasCalendarTarget ? this.calendarTarget : this.element
  }

  modeValueChanged() {
    if (!this.picker) return

    if (this.single && this.startTarget.value !== this.endTarget.value) {
      this.startTarget.value = ""
      this.endTarget.value = ""
    }
    this.teardown()
    this.build()
    if (!this.startTarget.value) this.dispatch("change", { detail: { start: "", end: "" } })
  }

  build() {
    const config = {
      mode: this.single ? "single" : "range",
      inline: true,
      // Always start with one month: flatpickr measures the calendar to size a
      // multi-month view, and this step is hidden until the wizard reaches it,
      // so measuring now would read zero. The observer adds the second month
      // once there is real width to measure.
      showMonths: 1,
      dateFormat: "Y-m-d",
      altInput: true,
      altFormat: "M j, Y",
      locale: { rangeSeparator: " — " },
      onChange: (selectedDates) => this.updateHiddenFields(selectedDates),
      // A new month's grid arrives on a short fade rather than a cut.
      onMonthChange: () => this.crossfadeDays(),
      onYearChange: () => this.crossfadeDays()
    }

    if (this.hasMinDateValue && this.minDateValue) config.minDate = this.minDateValue
    if (this.hasMaxDateValue && this.maxDateValue) config.maxDate = this.maxDateValue

    // Preload existing dates if present
    const existingDates = [this.startTarget.value, this.endTarget.value].filter(Boolean)
    if (existingDates.length > 0) config.defaultDate = this.single ? existingDates[0] : existingDates

    this.picker = flatpickr(this.displayTarget, config)
    this.picker.altInput.setAttribute("aria-label", this.single ? "Selected travel date" : "Selected travel dates")
    // Days and month arrows are spans, so they get their press feedback here.
    this.unpress = pressable(this.picker.calendarContainer, ".flatpickr-day:not(.flatpickr-disabled), .flatpickr-prev-month, .flatpickr-next-month", { scale: 0.92 })

    this.syncCopy()
    this.fitMonths(this.measured.clientWidth)
  }

  teardown() {
    this.unpress?.()
    this.unpress = null
    this.picker?.destroy()
    this.picker = null
  }

  // Headings, hints and placeholder for the mode.
  syncCopy() {
    this.rangeCopyTargets.forEach(el => el.hidden = this.single)
    this.singleCopyTargets.forEach(el => el.hidden = !this.single)
    const { rangePlaceholder, singlePlaceholder } = this.displayTarget.dataset
    const placeholder = (this.single ? singlePlaceholder : rangePlaceholder) || this.displayTarget.placeholder
    if (this.picker?.altInput) this.picker.altInput.placeholder = placeholder
  }

  crossfadeDays() {
    const days = this.picker?.daysContainer
    if (!days) return
    days.classList.add("is-changing")
    requestAnimationFrame(() => requestAnimationFrame(() => days.classList.remove("is-changing")))
  }

  // Show two months when they fit, one when they don't. A width of zero means
  // the step is hidden, which says nothing about fit, so leave it alone.
  fitMonths(width) {
    if (!this.picker || width === 0) return

    const months = width >= this.constructor.TWO_MONTHS_MIN_WIDTH ? 2 : 1
    if (months !== this.picker.config.showMonths) {
      this.picker.set("showMonths", months)
      // flatpickr only stamps this class while first building, not on set(),
      // and only writes pixel widths for two months, never clears them for one.
      this.picker.calendarContainer.classList.toggle("multiMonth", months > 1)
      if (months === 1) {
        this.picker.calendarContainer.style.removeProperty("width")
        this.picker.daysContainer.style.removeProperty("width")
      }
    }
  }

  updateHiddenFields(selectedDates) {
    if (this.single) {
      // One click is the whole answer: the day arrives and departs.
      const day = selectedDates[0] ? this.formatDate(selectedDates[0]) : ""
      this.startTarget.value = day
      this.endTarget.value = day
      if (day) landed()
    } else {
      // The arrival day clicks into place; the range landing is the commit.
      if (selectedDates.length === 1) detent()
      if (selectedDates.length >= 2) landed()

      this.startTarget.value = selectedDates.length >= 1 ? this.formatDate(selectedDates[0]) : ""
      this.endTarget.value = selectedDates.length >= 2 ? this.formatDate(selectedDates[1]) : ""
    }

    this.dispatch("change", { detail: { start: this.startTarget.value, end: this.endTarget.value } })
  }

  formatDate(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }
}
